import React, { useState, useEffect } from "react";
import { buildOtisFormFieldKey } from "../../utils/otisCombinationUtils";
import { formatNumber } from "../../utils/formatNumber";

// Define types for your data structures
interface Account {
  compNet: any;
  compGross: any;
  accountNumber: string;
  accountName: string;
  units?: number;
  ltdUnits?: number;
  pub_goal?: { $numberDecimal: string };
  currEst?: { $numberDecimal: string };
  suppEst?: { $numberDecimal: string };
  compInit?: string;
  Cpos_LTD?: any;
  _id: string;
}

interface Organization {
  organizationId: string;
  orgName: string;
  accounts?: Account[];
  _id: string;
}

interface RecordType {
  _id: string;
  DISTRIBUTION_CHANNEL?: string;
  DISTRIBUTION_CHANNEL_NAME?: string;
  organizations?: Organization[];
}

interface TableFooterProps {
  records: RecordType[];
  grandTotal: { [key: string]: number };
  formData: { [key: string]: any };
  headerHeight?: number;
}

// Define the type for our totals state
interface TotalsType {
  totalUnits: number;
  totalLtdUnits: number;
  pub_goal: number;
  currEst: number;
  suppEst: number;
  compInit: any;
  compGross: any;
  compNet: any;
  Cpos_LTD: any;
}

const STICKY_TOTALS_CLASS = "sticky z-[5] bg-gray-100 borderInputs border border-gray-300";

const TableFooter: React.FC<TableFooterProps> = ({ records, grandTotal, formData, headerHeight = 56 }) => {
  const [totals, setTotals] = useState<TotalsType>({
    totalUnits: 0,
    totalLtdUnits: 0,
    pub_goal: 0,
    currEst: 0,
    suppEst: 0,
    compInit: 0,
    compGross: 0,
    compNet: 0,
    Cpos_LTD: 0
  });
  const parseNumeric = (val: any): number =>
  typeof val === 'object' && val?.$numberDecimal
    ? parseFloat(val.$numberDecimal)
    : parseFloat(val || '0');

  const parseNumber = (val: any): number | null => {
    if (val == null) return null;
    const raw =
      typeof val === "object" && val?.$numberDecimal != null ? val.$numberDecimal : val;
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const hasMeaningfulValue = (val: any): boolean => {
    const n = parseNumber(val);
    return n != null && n !== 0;
  };

  const getMainValue = (record: any, field: "pub_goal" | "currEst" | "suppEst"): any => {
    if (field === "pub_goal") return record.main_pubgoal;
    if (field === "currEst") return record.main_current_estimate;
    return record.main_supplemental_estimate;
  };

  const getOrgValue = (org: any, field: "pub_goal" | "currEst" | "suppEst"): any => {
    if (field === "pub_goal") return org.org_pub_goal;
    if (field === "currEst") return org.org_current_estimate;
    return org.org_supplemental_estimate;
  };

  const getPreferredTotalForRecord = (
    record: any,
    field: "pub_goal" | "currEst" | "suppEst"
  ): number => {
    // 1) Distribution (main) overrides everything else
    const mainKey = `${record._id}---${field}`;
    const mainVal = formData?.[mainKey] ?? getMainValue(record, field);
    if (hasMeaningfulValue(mainVal)) return parseNumber(mainVal) ?? 0;

    // 2) Otherwise sum Organization-level values (if any exist)
    let hasAnyOrg = false;
    let orgSum = 0;
    record.organizations?.forEach((org: any) => {
      const orgKey = buildOtisFormFieldKey({
        recordId: record._id,
        orgId: org.organizationId,
        orgName: org.orgName,
        field,
        isOrgLevelRow: true,
      });
      const orgVal = formData?.[orgKey] ?? getOrgValue(org, field);
      if (hasMeaningfulValue(orgVal)) {
        hasAnyOrg = true;
        orgSum += parseNumber(orgVal) ?? 0;
      }
    });
    if (hasAnyOrg) return orgSum;

    // 3) Fallback: sum Account-level values
    let accSum = 0;
    record.organizations?.forEach((org: any) => {
      org.accounts?.forEach((account: any) => {
        const accKey = buildOtisFormFieldKey({
          recordId: record._id,
          orgId: org.organizationId,
          orgName: org.orgName,
          accountId: account.accountNumber,
          accountName: account.accountName,
          field,
        });
        const accVal = formData?.[accKey] ?? account?.[field];
        accSum += parseNumber(accVal) ?? 0;
      });
    });
    return accSum;
  };

  useEffect(() => {
    const newTotals = records.reduce<TotalsType>(
      (acc, record) => {
        record.organizations?.forEach((org) => {
          org.accounts?.forEach((account) => {
            acc.totalUnits += Number(account.units) || 0;
            acc.totalLtdUnits += Number(account.ltdUnits) || 0;
            acc.compInit += parseNumeric(account.compInit);
            acc.compGross += parseNumeric(account.compGross);
            acc.compNet += parseNumeric(account.compNet);
            acc.Cpos_LTD += parseNumeric(account.Cpos_LTD);
          });
        });

        acc.pub_goal += getPreferredTotalForRecord(record, "pub_goal");
        acc.currEst += getPreferredTotalForRecord(record, "currEst");
        acc.suppEst += getPreferredTotalForRecord(record, "suppEst");
        return acc;
      },
      {
        totalUnits: 0,
        totalLtdUnits: 0,
        pub_goal: 0,
        currEst: 0,
        suppEst: 0,
        compInit: 0,
        compGross: 0,
        compNet: 0,
        Cpos_LTD: 0
      }
    );

    setTotals(newTotals);
  }, [records, grandTotal, formData]);

  const stickyStyle = { top: headerHeight };
  return (
    <tbody className="bg-gray-100">
      <tr>
        <th
          colSpan={2}
          style={{ ...stickyStyle, position: "sticky", left: "var(--frz-l1)", zIndex: 8, borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }}
          className={`px-2 py-2 font-semibold text-left bg-gray-100 borderInputs borderInputs-first min-w-[150px] overflow-hidden text-ellipsis align-middle`}
        >
          <div className="text-center align-middle">Total</div>
        </th>
        <th style={{ ...stickyStyle, position: "sticky", left: "var(--frz-l3)", zIndex: 8, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }} className={`px-2 py-2 font-semibold text-center borderInputs min-w-[100px] bg-gray-100 align-middle`}>
          {formatNumber(totals.totalUnits)}
        </th>
        <th style={{ ...stickyStyle, position: "sticky", left: "var(--frz-l4)", zIndex: 8, borderRight: "1px solid #ccc", borderBottom: "1px solid #ccc" }} className={`px-2 py-2 font-semibold text-center borderInputs min-w-[100px] bg-gray-100 align-middle`}>
          {formatNumber(totals.totalLtdUnits)}
        </th>
        
        {/* Empty cells for fields we're not showing */}
        <th style={stickyStyle} className={`px-4 py-2 borderInputs ${STICKY_TOTALS_CLASS}`}></th>
        
        {/* PUB GOAL */}
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center borderInputs ${STICKY_TOTALS_CLASS} hidden`}>
          {formatNumber(totals.pub_goal)}
        </th>
        
        {/* CURRENT ESTIMATE */}
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.currEst)}
        </th>
        
        {/* 6-MONTH ESTIMATE (INCL LAYDOWN) */}
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.suppEst)}
        </th>
        
        {/* COMP INITIAL ORDER */}
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.compInit)}
        </th>
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.compGross)}
        </th>
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.compNet)}
        </th>
        
        <th style={stickyStyle} className={`px-4 py-2 font-semibold text-center ${STICKY_TOTALS_CLASS}`}>
          {formatNumber(totals.Cpos_LTD)}
        </th>
      </tr>
    </tbody>
  );
};

export default TableFooter;



// import React, { useState, useEffect } from "react";
// import { OTISRecords } from "./OtisHelper";

// // Define types for your data structures
// interface Account {
//   accountNumber: string;
//   accountName: string;
//   units?: number;
//   sales_notes?: string;
//   pub_goal?: { $numberDecimal: string };
//   printmtdDate?: { $numberDecimal: string };
//   currEst?: { $numberDecimal: string };
//   suppEst?: { $numberDecimal: string };
//   compInit?: { $numberDecimal: string };
//   compGross?: { $numberDecimal: string };
//   compNet?: { $numberDecimal: string };
//   retPercentage?: { $numberDecimal: string };
//   cpos4wkPOS?: { $numberDecimal: string };
//   cpos8wkPOS?: { $numberDecimal: string };
//   Cpos_LTD?: { $numberDecimal: string };
//   status?: number;
//   _id: string;
// }

// interface Organization {
//   organizationId: string;
//   orgName: string;
//   accounts?: Account[];
//   _id: string;
// }

// interface RecordType {
//   _id: string;
//   DISTRIBUTION_CHANNEL?: string;
//   DISTRIBUTION_CHANNEL_NAME?: string;
//   organizations?: Organization[];
// }

// interface TableFooterProps {
//   records: RecordType[];
//   grandTotal: { [key: string]: number };
// }

// // Define the type for our totals state
// type TotalsType = {
//   totalUnits: number;
// } & Record<string, number>;

// const TableFooter: React.FC<TableFooterProps> = ({ records, grandTotal }) => {
//   // Initialize totals for all fields
//   const initialTotals: TotalsType = Object.keys(OTISRecords).slice(4).reduce((acc, field) => {
//     acc[field] = 0;
//     return acc;
//   }, { totalUnits: 0 } as TotalsType);

//   const [totals, setTotals] = useState<TotalsType>(initialTotals);

//   useEffect(() => {
//     // Calculate totals whenever records or grandTotal changes
//     const newTotals = records.reduce(
//       (acc: TotalsType, record: RecordType) => {
//         record.organizations?.forEach((org: Organization) => {
//           org.accounts?.forEach((account: Account) => {
//             acc.totalUnits += account.units || 0;
            
//             // Sum all OTISRecords fields
//             Object.keys(OTISRecords).slice(4).forEach((field: string) => {
//               const fieldValue = account[field as keyof Account] 
//                 ? parseInt(
//                     (account[field as keyof Account] as { $numberDecimal: string })?.$numberDecimal || '0'
//                   ) 
//                 : 0;
//               acc[field] += fieldValue || 0;
//             });
//           });
//         });
//         return acc;
//       },
//       { ...initialTotals }
//     );

//     setTotals(newTotals);
//   }, [records, grandTotal]);

//   return (
//     <tfoot className="bg-gray-100 sticky bottom-0">
//       <tr>
//         <th colSpan={3} className="px-4 py-2 border border-gray-300 font-semibold text-left">
//           Total
//         </th>
//         <th className="px-4 py-2 border border-gray-300 font-semibold text-center">
//           {totals.totalUnits}
//         </th>
        
//         {/* Render totals for all OTISRecords fields */}
//         {Object.keys(OTISRecords).slice(4).map((field) => (
//           <th key={field} className="px-4 py-2 border border-gray-300 font-semibold text-center">
//             {totals[field] || 0}
//           </th>
//         ))}
//       </tr>
//     </tfoot>
//   );
// };

// export default TableFooter;